import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { authFetch } from '@/lib/auth';
import { StepShell } from '@/components/signup/StepShell';
import { MobileAccountSetup } from '@/components/booking/MobileAccountSetup';
import { OptionButton, useAutoFocus } from '@/components/booking/fields';
import { buildHomeSteps, HomeSummary, homePackage } from '@/components/booking/homeSteps';
import { buildLandSteps, LandSummary, Guarantee } from '@/components/booking/landSteps';
import { buildQuoteSteps, buildCommercialSteps, buildContactStep, QuoteSummary, toQuoteForm } from '@/components/booking/quoteSteps';
import { BookDone } from '@/components/booking/BookDone';
import { formatPhone } from '@/pages/Register';
import { buildOrderPayload } from '@/lib/realEstateOrder';
import { landPrice } from '@/lib/vacantLand';
import { KINDS, PROPERTY_TYPES, RE_KINDS, ALL_KINDS, QUOTE_LABELS, loadBookDraft, saveBookDraft, clearBookDraft, apiError } from '@/lib/bookFlow';
import SEO from '@/components/SEO';
import { Loader2 } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const isKind = (k) => ALL_KINDS.includes(k);
const isRealEstate = (k) => k === 'real_estate' || RE_KINDS.includes(k);
const ACCOUNT_SCREENS = 5;
const hasProfileContact = (u) => !!(u?.full_name?.trim() && (u?.phone || '').replace(/\D/g, '').length === 10);

const initialDraft = (params) => {
  const d = loadBookDraft();
  const kind = isKind(params.get('type')) ? params.get('type') : params.get('resume') ? d.kind : '';
  const pkg = params.get('package');
  const home = pkg ? { ...d.home, useCreatorSpotlight: pkg === 'creator_spotlight' } : d.home;
  return { ...d, kind, home };
};

const postJson = (path, body) => authFetch(`${API_BASE}${path}`, {
  method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
});

const submitters = {
  home: async ({ home }) => {
    const { data, error } = await apiError(await postJson('/api/real-estate/orders', buildOrderPayload(home)));
    return error ? { error } : { done: { kind: 'home', orderNumber: `RE-${data.id.split('-')[0].toUpperCase()}`, pkg: homePackage(home) } };
  },
  land: async ({ land: f, contact }) => {
    const { data, error } = await apiError(await postJson('/api/vacant-land/orders', {
      parcel_address: f.parcelAddress.trim() || null, apn: f.apn.trim() || null, city: f.city.trim(), zip_code: f.zipCode.trim(),
      acreage: parseFloat(f.acreage), location: f.location, preferred_date: f.preferredDate, access_notes: f.accessNotes.trim() || null,
      contact_name: contact.contactName.trim(), contact_phone: contact.contactPhone, add_video: f.addVideo && !landPrice(f).quote,
      origin_url: window.location.origin,
    }));
    if (error) return { error };
    return data.quote ? { done: { kind: 'land-quote' } } : { redirect: data.checkout_url };
  },
  quote: async (draft, user) => {
    const { kind, contact } = draft;
    const f = toQuoteForm(kind, draft);
    const { error } = await apiError(await postJson('/api/orders/', {
      service_type: 'general_upload', title: `Quote request (${QUOTE_LABELS[kind]}): ${f.service}`.slice(0, 100),
      notes: f.description.trim(),
      details: {
        services_requested: [f.service], quote_request: true, project_type: QUOTE_LABELS[kind], project_description: f.description.trim(),
        location: f.location.trim(), timeline: f.timeline || null, budget: f.budget || null,
        contact_name: contact.contactName.trim(), contact_email: user?.email || null, contact_phone: contact.contactPhone,
      },
    }));
    return error ? { error } : { done: { kind: 'quote' } };
  },
};

const reviewStep = (draft) => {
  const { kind } = draft;
  if (kind === 'home') {
    const pkg = homePackage(draft.home);
    return { title: 'Review your order', continueLabel: `Submit order — $${pkg.price}, invoiced after the shoot`, loadingLabel: 'Submitting your order…' };
  }
  if (kind === 'land') {
    const p = landPrice(draft.land);
    return { title: 'Review and pay', continueLabel: p.quote ? 'Request a quote' : `Pay $${p.total} & book`, loadingLabel: p.quote ? 'Sending…' : 'Opening secure checkout…' };
  }
  return { title: 'Review your request', continueLabel: 'Request a quote', loadingLabel: 'Sending…' };
};

export default function Book() {
  const { isAuthenticated, loading: authLoading, user } = useAuth();
  const [params] = useSearchParams();
  const [draft, setDraft] = useState(() => initialDraft(params));
  const [index, setIndex] = useState(() => {
    const t = params.get('type');
    return RE_KINDS.includes(t) ? 2 : isKind(t) ? 1 : 0;
  });
  const [direction, setDirection] = useState(1);
  const [error, setError] = useState(params.get('checkout') === 'cancel' ? 'Payment was cancelled — your answers are saved. You can try again.' : '');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const resumed = useRef(false);
  const { kind } = draft;
  const ref = useAutoFocus(`${kind}-${index}`);

  useEffect(() => { saveBookDraft(draft); }, [draft]);
  useEffect(() => {
    if (!user) return;
    setDraft((d) => ({ ...d, contact: {
      contactName: d.contact.contactName || user.full_name || '',
      contactPhone: d.contact.contactPhone || (user.phone ? formatPhone(user.phone) : ''),
    } }));
  }, [user]);

  const clear = () => setError('');
  const setIn = (section) => (k, v) => { setDraft((d) => ({ ...d, [section]: { ...d[section], [k]: v } })); clear(); };
  const go = (to) => { clear(); setDirection(to > index ? 1 : -1); setIndex(to); window.scrollTo({ top: 0 }); };

  const chooser = {
    key: 'kind', title: 'What do you need shot?',
    validate: () => (kind ? null : 'Please choose what you need shot'),
    body: KINDS.map((k) => (
      <OptionButton key={k.value} selected={k.value === 'real_estate' ? isRealEstate(kind) : kind === k.value} testId={`book-kind-${k.value.replace('_', '-')}`}
        onClick={() => { setDraft((d) => ({ ...d, kind: k.value === 'real_estate' ? (RE_KINDS.includes(d.kind) ? d.kind : d.reKind || 'real_estate') : k.value })); go(1); }}>
        <span className="block font-medium py-0.5">{k.label}</span>
        <span className="block text-sm text-[#E8E4DE]/60">{k.description}</span>
      </OptionButton>
    )),
  };
  const propertyStep = {
    key: 'property', title: 'What type of property?',
    validate: () => (RE_KINDS.includes(kind) ? null : 'Please choose the property type'),
    body: PROPERTY_TYPES.map((p) => (
      <OptionButton key={p.value} selected={kind === p.value} testId={`book-property-${p.value}`}
        onClick={() => { setDraft((d) => ({ ...d, kind: p.value, reKind: p.value })); go(2); }}>
        <span className="block font-medium py-0.5">{p.label}</span>
        <span className="block text-sm text-[#E8E4DE]/60">{p.description}</span>
      </OptionButton>
    )),
  };
  const resolved = kind && kind !== 'real_estate';
  const stepArgs = { ref };
  const kindSteps = kind === 'home' ? buildHomeSteps({ ...stepArgs, form: draft.home, set: setIn('home'), error })
    : kind === 'land' ? buildLandSteps({ ...stepArgs, form: draft.land, set: setIn('land') })
      : kind === 'commercial' ? buildCommercialSteps({ ...stepArgs, form: draft.commercial, set: setIn('commercial') })
        : resolved ? buildQuoteSteps({ ...stepArgs, kind, form: draft[kind], set: setIn(kind) }) : [];
  const needsContact = resolved && kind !== 'home' && !hasProfileContact(user);
  const contactStep = buildContactStep({ contact: draft.contact, setContact: setIn('contact'), ref });
  const review = {
    key: 'review', validate: () => null, ...reviewStep(draft),
    body: kind === 'home' ? <HomeSummary form={draft.home} set={setIn('home')} />
      : kind === 'land' ? <><LandSummary form={draft.land} contact={draft.contact} /><Guarantee /></>
        : <QuoteSummary kind={kind} form={toQuoteForm(kind, draft)} contact={draft.contact} />,
  };
  const tail = isAuthenticated ? [...(needsContact ? [contactStep] : []), review] : [{ key: 'account' }];
  const flow = [chooser, ...(isRealEstate(kind) ? [propertyStep] : []), ...kindSteps, ...(resolved ? tail : [])];
  // Progress bar total: real flow once the choice is known, else the residential estimate; signed-out adds the account screens.
  const knownLength = resolved ? flow.length : 3 + buildHomeSteps({ form: draft.home, set: () => {}, ref }).length;
  const total = knownLength + (isAuthenticated ? 0 : ACCOUNT_SCREENS);

  // Coming back from login, signup or a cancelled checkout: jump to the first unanswered step.
  useEffect(() => {
    if (resumed.current || authLoading || !isAuthenticated || !resolved) return;
    if (!params.get('resume') && params.get('checkout') !== 'cancel') return;
    resumed.current = true;
    const firstOpen = flow.findIndex((s) => s.validate && s.validate());
    setIndex(firstOpen > 0 ? firstOpen : flow.length - 1);
  }, [authLoading, isAuthenticated, kind]);

  if (authLoading) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#C6A14A]" /></div>;
  if (done) return <BookDone {...done} />;

  const i = Math.min(index, flow.length - 1);
  const step = flow[i];
  const seo = <SEO title="Book a Shoot | RC Studios" description="Book home photos, vacant land drone photos, video or another service with RC Studios in Kingman, AZ." />;

  if (step.key === 'account') {
    return <>{seo}<MobileAccountSetup returnTo={`/book?type=${kind}&resume=1`} onExit={() => go(i - 1)} offset={i} progressTotal={total} /></>;
  }

  const submit = async () => {
    setSubmitting(true);
    clear();
    try {
      const res = await submitters[kind === 'home' || kind === 'land' ? kind : 'quote'](draft, user);
      if (res.redirect) { window.location.href = res.redirect; return; }
      if (res.error) { setError(res.error); setSubmitting(false); return; }
      clearBookDraft(kind);
      setDone(res.done);
      window.scrollTo({ top: 0 });
    } catch {
      setError('Something went wrong. Please check your connection and try again.');
    }
    setSubmitting(false);
  };

  const next = () => {
    const err = step.validate();
    if (err) { setError(err); return; }
    if (step.key === 'review') submit();
    else go(i + 1);
  };

  return (
    <>{seo}
      <StepShell wide eyebrow="Book a shoot" step={i + 1} total={total} progressOnly direction={direction} title={step.title} hint={step.hint}
        error={step.ownsError ? '' : error} onBack={i > 0 ? () => go(i - 1) : null} onSubmit={next} loading={submitting}
        continueLabel={step.continueLabel} loadingLabel={step.loadingLabel || 'Please wait…'}>
        {step.body}
      </StepShell>
    </>
  );
}
